// module imports
import {
  contactFromIncomingEvent,
  numberFromContactSimple,
} from 'modules/Contact'
import { parkedSummary } from 'modules/SessionSummary/Summary'
import {
  blockNumber,
  showErrorSnackbar,
} from 'modules/GlobalEvents/EventDispatchers'
import eventLogger from 'modules/Logger'
import { makeCall, referToCode, transferSession } from 'modules/Sip'
import {
  getActiveSession,
  getActiveSessionIndex,
  getMergedSessionsInstances,
  thereIsHoldTogglingSession,
} from 'modules/Sip/Helpers'
import {
  removeAudioStream,
  startAlreadyMergedAudioStream,
  startAudioStream,
  startConferenceAudioStream,
} from 'modules/Audio/AudioStream'
// redux imports
import { addSipAction } from 'redux/Config'
// constant imports
import { REFER_CODES } from 'constants/Sessions'
import { SipActionType } from 'constants/SipActions'
import { preparePhoneNumberForCall } from 'n2p-ui-library/utils/phone-number/phone-number.util'
import * as C from './constants'
import {
  connectedMergedSessionsSelector,
  notHoldSessionsSelector,
} from './selectors'
import { isCSV3 } from '../../modules/Helpers'

export const holdActiveSession =
  (needToRemove = true) =>
  (dispatch, getState) => {
    return new Promise((resolve) => {
      const mergedSessions = connectedMergedSessionsSelector(getState())

      // remove audio
      needToRemove && removeAudioStream()

      const activeSessions = notHoldSessionsSelector(getState())
      let counter = 0

      activeSessions.forEach((session) => {
        dispatch({ type: C.ADD_HOLD_TOGGLING, payload: session.id })

        session.session.hold(undefined, () => {
          dispatch({ type: C.REMOVE_HOLD_TOGGLING, payload: session.id })

          counter += 1

          if (counter === activeSessions.length) {
            resolve()
          }
        })

        dispatch({ type: C.HOLD_ACTIVE, payload: session.id })
      })

      if (mergedSessions.length) {
        dispatch({ type: C.HOLD_MERGED })
      }
    })
  }

export const createOutgoing = () => (dispatch) => {
  makeCall()

  dispatch({ type: C.OUTGOING_CREATE })
}

export const connecting = (index = 0) => ({
  type: C.CONNECTING,
  payload: index,
})

/**
 * Called when an outgoing call is accepted by the remote party
 *
 * @param index
 * @returns {function(*): void}
 */
export const accepted =
  (index = 0) =>
  (dispatch) => {
    dispatch(holdActiveSession(false))

    dispatch({
      type: C.ACCEPTED,
      payload: index,
    })
  }

export const mediaConnected =
  (index = 0) =>
  (dispatch) => {
    dispatch({
      type: C.MEDIA_CONNECTED,
      payload: index,
    })
  }

export const setPlayLocalRingTone =
  (index = 0) =>
  (dispatch) => {
    dispatch({
      type: C.IS_PLAY_LOCAL_RING_TONE,
      payload: index,
    })
  }

export const addSession = (session, id, request) => (dispatch, getState) => {
  // TODO move all to single `incoming`
  const { direction } = session
  const outgoing = direction === C.outgoing
  const incoming = direction === C.incoming

  // set audio track
  if (outgoing) {
    startAudioStream(session.connection, id, getState)
  }

  const participant = incoming
    ? contactFromIncomingEvent(session, request)
    : getState().contacts.selected

  dispatch({
    type: C.SESSION_ADD,
    payload: {
      id,
      session,
      participant,
      direction,
      [C.outgoing]: outgoing,
      [C.incoming]: incoming,
      connecting: true,
      connected: false,
      holdToggling: false,
    },
  })
}

/**
 * End call triggers a session ended event which
 * handles removing the session from state*
 */
export const sessionTerminate =
  (index = 0) =>
  async (dispatch, getState) => {
    const { session, incoming } = getState().sessions.list[index]

    const options = incoming ? C.decline : {}
    let error

    try {
      await session.terminate(options)
    } catch (err) {
      // can throw INVALID_STATE_ERROR
      eventLogger.error('INVALID_STATE_ERROR', err)
      error = err
    }

    dispatch({
      type: C.SESSION_TERMINATED,
      payload: { error, index },
    })
  }

export const allSessionsTerminate = () => (dispatch, getState) => {
  getState()
    .sessions.list.filter(Boolean)
    .forEach((session, index) => {
      dispatch(sessionTerminate(index))
    })
}

export const sessionEnd = (data, index) => {
  return {
    type: C.SESSION_ENDED,
    payload: index,
  }
}

export const sessionTransfer =
  (index = 0, transferToNumber) =>
  (dispatch) => {
    transferSession(index, transferToNumber)

    dispatch({ type: C.SESSION_TRANSFER, payload: index })
  }

export const activeSessionTransfer = () => (dispatch, getState) => {
  const { activeSessionIndex: activeId } = getState().sessions

  if (activeId >= 0) {
    dispatch(sessionTransfer(getState().sessions.activeSessionIndex))
  }
}

export const sessionTransferToVoicemail =
  (index = 0, transferToNumber) =>
  (dispatch) => {
    referToCode(
      index,
      REFER_CODES.TRANSFER_TO_VOICEMAIL.ACTION,
      transferToNumber,
    )

    dispatch({ type: C.SESSION_TRANSFER_VOICEMAIL, payload: index })
  }

export const setWarmTransfer = (index = 0, isPending = false) => ({
  type: C.SESSION_TRANSFER_WARM_SET,
  payload: { index, isPending },
})

export const createOutgoingWithWarmTransfer =
  (index = 0, isPending = false) =>
  (dispatch) => {
    dispatch(setWarmTransfer(index, isPending))

    makeCall(true)

    dispatch({ type: C.OUTGOING_CREATE })
  }

export const warmTransferPending = (id) => ({
  type: C.SESSION_TRANSFER_WARM_PENDING,
  payload: id,
})

// TODO implement graceful fail
export const warmTransferFulfilled = (ids) => (dispatch, getState) => {
  if (ids.length !== 2) {
    return
  }

  const participant = getState().sessions.list[ids[1]]?.participant

  // validate participant
  if (!participant) return

  const transferToNumber = numberFromContactSimple(participant)

  // do transfer
  transferSession(ids[0], transferToNumber, true, ids[1])

  dispatch({
    type: C.SESSION_TRANSFER_WARM_FULFILLED,
    payload: ids,
  })
}

export const blockIncoming =
  (index = 0) =>
  (dispatch, getState) => {
    const { participant } = getState().sessions.list[index]

    dispatch(sessionTerminate(index))

    blockNumber(preparePhoneNumberForCall(participant.phone))

    eventLogger.info('incoming_block', participant)

    dispatch({ type: C.INCOMING_BLOCK })
  }

export const muteToggle = () => (dispatch, getState) => {
  const session = getActiveSession(getState)
  if (!session) return

  session.isMuted().audio ? session.unmute() : session.mute()

  dispatch({
    type: C.MUTE_TOGGLE,
    payload: session.isMuted().audio,
  })
}

export const unholdActiveSession = () => (dispatch, getState) => {
  const session = getActiveSession(getState)
  const index = getActiveSessionIndex(getState)
  if (!session) return

  if (session.isOnHold().local) {
    dispatch({ type: C.ADD_HOLD_TOGGLING, payload: index })

    session.unhold(undefined, () => {
      dispatch({ type: C.REMOVE_HOLD_TOGGLING, payload: index })
    })
  }

  startAudioStream(session.connection, index, getState)

  dispatch({
    type: C.UNHOLD_ACTIVE,
    payload: session.isOnHold().local,
  })
}

export const holdToggle = () => (dispatch, getState) => {
  const session = getActiveSession(getState)
  const id = getActiveSessionIndex(getState)

  if (!session) return

  if (session.isOnHold().local) {
    session.unhold()
    startAudioStream(session.connection, id, getState)
  } else {
    session.hold()
  }

  dispatch({
    type: C.HOLD_TOGGLE,
    payload: session.isOnHold().local,
  })
}

export const acceptIncoming =
  (index = 0) =>
  (dispatch, getState) => {
    const { sessionOptions } = getState().sip
    const { session } = getState().sessions.list[index]

    // put active session on hold
    dispatch(holdActiveSession())

    dispatch(addSipAction(SipActionType.CALL_ANSWER))

    try {
      session.answer(sessionOptions)
    } catch (e) {
      eventLogger.info('accept_incoming', e)
    }

    dispatch({
      type: C.INCOMING_ACCEPT,
      payload: index,
    })
  }

export const transferToggle = () => ({ type: C.TRANSFER_TOGGLE })

export const dialpadToggle = () => ({ type: C.DIALPAD_TOGGLE })

export const addCallToggle = () => ({ type: C.ADD_CALL_TOGGLE })

export const actionClear = () => ({ type: C.ACTION_CLEAR })

export const activeSessionToggle =
  (index, unhold = true) =>
  (dispatch, getState) => {
    if (!thereIsHoldTogglingSession(getState)) {
      dispatch(holdActiveSession())

      dispatch({
        type: C.SESSION_ACTIVE_TOGGLE,
        payload: index,
      })

      if (unhold) {
        dispatch(unholdActiveSession())
      }
    }
  }

export const muteToggleMerged = () => (dispatch, getState) => {
  const sessions = getMergedSessionsInstances(getState)
  const { mute } = getState().sessions.merged

  sessions.forEach((session) => {
    mute ? session.unmute() : session.mute()
  })

  dispatch({
    type: C.MUTE_TOGGLE_MERGED,
    payload: !mute,
  })
}

export const holdToggleMerged = () => (dispatch, getState) => {
  const sessions = getMergedSessionsInstances(getState)
  const { hold } = getState().sessions.merged

  sessions.forEach((session) => {
    hold ? session.unhold() : session.hold()
  })

  hold && startAlreadyMergedAudioStream(getState)

  dispatch({
    type: C.HOLD_TOGGLE_MERGED,
    payload: !hold,
  })
}

export const unHoldMergedSessions = () => (dispatch, getState) => {
  const sessions = getMergedSessionsInstances(getState)

  sessions.forEach((session) => {
    if (session.isOnHold().local) {
      session.unhold()
    }
  })

  startAlreadyMergedAudioStream(getState)

  dispatch({ type: C.UNHOLD_MERGED })
}

export const unMuteMergedSessions = () => (dispatch, getState) => {
  const sessions = getMergedSessionsInstances(getState)

  sessions.forEach((session) => {
    if (session.isMuted().audio) {
      session.unmute()
    }
  })

  dispatch({ type: C.UNMUTE_MERGED })
}

export const mixMergedTracks = () => (dispatch, getState) => {
  const { tracksAreMixed } = getState().sessions.merged

  if (!tracksAreMixed) {
    const sessions = getMergedSessionsInstances(getState)
    startConferenceAudioStream(sessions, dispatch)

    dispatch({ type: C.TRACKS_MIX_MERGED })
  }
}

let mixTracksTimeout

export const mixMergedTracksWithDelay = () => (dispatch, getState) => {
  const { needToMixWithDelay } = getState().sessions.merged

  window.clearTimeout(mixTracksTimeout)

  if (needToMixWithDelay) {
    mixTracksTimeout = window.setTimeout(() => {
      dispatch(mixMergedTracks())
    }, 100)
  }
}

export const mergeSessions =
  (ids = []) =>
  async (dispatch, getState) => {
    // validate we have multiple sessions
    if (ids.length < 2) return

    dispatch({
      type: C.SESSIONS_MERGE,
      payload: ids,
    })

    const activeSessionIndex = getActiveSessionIndex(getState)

    if (!ids.includes(activeSessionIndex)) {
      await dispatch(holdActiveSession())
      dispatch(activeSessionToggle(ids[0]), false)
    }
    dispatch(unHoldMergedSessions())
    dispatch(unMuteMergedSessions())

    dispatch({ type: C.ADD_DELAY_MIX_MERGED })
  }

export const terminateMergedSessions = () => async (dispatch, getState) => {
  const sessions = getMergedSessionsInstances(getState)

  const errors = []

  sessions.forEach((session) => {
    try {
      session.terminate()
      eventLogger.info('TERMINATED_MERGED')
    } catch (err) {
      // can throw INVALID_STATE_ERROR
      eventLogger.error('INVALID_STATE_ERROR_TERMINATING_MERGE', err)
      errors.push(err)
    }
  })

  dispatch({
    type: C.SESSIONS_MERGED_TERMINATED,
    payload: errors,
  })
}

export const silenceRinger = () => ({ type: C.INCOMING_SILENCE })

export const parkedSessionTerminate = (parkedSessionId) => ({
  type: C.SESSION_PARK_TERMINATED,
  payload: parkedSessionId,
})

export const parkSessionReject = (sessionIndex) => ({
  type: C.SESSION_PARK_REJECTED,
  payload: sessionIndex,
})

export const parkSession = (id) => (dispatch) => {
  referToCode(id, REFER_CODES.PARK.ACTION)

  dispatch({
    type: C.SESSION_PARK_PENDING,
    payload: {
      id,
      timeout: isCSV3()
        ? setTimeout(() => {
            dispatch(parkSessionReject(id))
            dispatch(sessionEnd({}, id))
            showErrorSnackbar('NOTIFICATIONS.PARK_FAILED', 'call-failed')
          }, 15000)
        : null,
    },
  })
}

export const sessionParked = (id, slot) => (dispatch, getState) => {
  const session = getState().sessions.list[id]

  if (!session) return

  const summary = parkedSummary(session, slot)

  dispatch({
    type: C.SESSION_PARK_FULFILLED,
    payload: {
      ...summary,
      sessionId: id,
    },
  })
}

export const unparkedSessionElsewhere = (id) => (dispatch, getState) => {
  // validate exists
  if (!getState().sessions.parked[id]) return

  dispatch({ type: C.SESSION_UNPARKED_ELSEWHERE, payload: id })
}

export const unparkSession = (id) => (dispatch, getState) => {
  // validate exists
  if (!getState().sessions.parked[id]) return

  // hold active session
  dispatch(holdActiveSession())

  // make call
  dispatch(createOutgoing())

  // remove from parked list
  dispatch({ type: C.SESSION_UNPARKED, payload: id })
}
